import { createConsumer, Consumer, Subscription } from "@rails/actioncable";
import { useEffect, useRef } from "react";

/**
 * ---- Internal caches ----
 */
const consumerCache = new Map<string, Consumer>();
const subscriptionCache = new Map<string, Subscription>();
const refCount = new Map<string, number>();

/**
 * Create or reuse a Consumer
 */
function getConsumer(url: string, protocols?: string[]): Consumer {
  const key = `${url}|${protocols?.join(",") ?? ""}`;

  if (!consumerCache.has(key)) {
    consumerCache.set(
      key,
      createConsumer(url, protocols)
    );
  }

  return consumerCache.get(key)!;
}

/**
 * Build a stable cache key for subscriptions
 */
function subscriptionKey(
  channel: string,
  params: Record<string, unknown>
) {
  return `${channel}:${JSON.stringify(params)}`;
}

/**
 * Public hook
 */
export function useActionCable<TPayload>(
  options: {
    url: string;
    channel: string;
    params?: Record<string, unknown>;
    protocols?: string[];
    onReceived: (data: TPayload) => void;
    onConnected?: () => void;
    onDisconnected?: () => void;
  }
) {
  const {
    url,
    channel,
    params = {},
    protocols,
    onReceived,
    onConnected,
    onDisconnected,
  } = options;

  const receivedRef = useRef(onReceived);
  receivedRef.current = onReceived;

  useEffect(() => {
    const consumer = getConsumer(url, protocols);
    const key = subscriptionKey(channel, params);

    if (!subscriptionCache.has(key)) {
      const subscription = consumer.subscriptions.create(
        { channel, ...params },
        {
          connected: () => onConnected?.(),
          disconnected: () => onDisconnected?.(),
          received: (data: TPayload) => receivedRef.current(data),
        }
      );

      subscriptionCache.set(key, subscription);
      refCount.set(key, 1);
    } else {
      refCount.set(key, (refCount.get(key) || 0) + 1);
    }

    return () => {
      const count = (refCount.get(key) || 1) - 1;
      refCount.set(key, count);

      if (count <= 0) {
        subscriptionCache.get(key)?.unsubscribe();
        subscriptionCache.delete(key);
        refCount.delete(key);
      }
    };
  }, [url, channel, JSON.stringify(params), protocols?.join(",")]);
}

